import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the FormsMasterContext
const FormsMasterContext = createContext();

// Custom hook to use the FormsMasterContext
export const useFormsMaster = () => useContext(FormsMasterContext);

// Provider component to wrap the app
export const FormsMasterProvider = ({ children }) => {
  const [purposes, setPurposes] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/masters/purposes"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPurposes = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setDepartments(response.data?.data || {}); // Access `data.data` for actual departments master
      const { data, current_page, last_page, per_page, total } = response.data;

      setPurposes(data || []); // Update leave types list

      setPagination({
          currentPage: current_page,
          totalPages: last_page,
          totalItems: total,
          perPage: per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching purpose master:", error);
      setError(error.response?.data?.message || "Failed to fetch purpose.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch purposes master from the API on load
  useEffect(() => {
    fetchPurposes();
  }, [API_URL, token]);

  // Add a new department
  const addPurpose = async (newPurpose) => {
    try {
      const response = await axios.post(API_URL, newPurpose, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setPurposes((prev) => [...prev, response.data?.data]);
      setPurposes((prev) => [response.data?.data, ...prev]);

    } catch (error) {
      console.error("Error adding purpose:", error);
    }
  };

  // Update an existing department
  const updatePurpose = async (updatedPurpose) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedPurpose.id}`, updatedPurpose, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setPurposes((prev) =>
        prev.map((purpose) =>
          purpose.id === updatedPurpose.id ? { ...purpose, ...response.data?.data } : purpose
        )
      );
    } catch (error) {
      console.error("Error updating purpose master:", error);
    }
  };

  // Delete a department
  const deletePurpose = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setPurposes((prev) => prev.filter((purpose) => purpose.id !== id));
    } catch (error) {
      console.error("Error deleting purpose:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      purposes,
      addPurpose,
      updatePurpose,
      deletePurpose,
    }),
    [purposes, isLoading] // Recalculate the value only when `departments` changes
  );

  return <FormsMasterContext.Provider value={value}>{children}</FormsMasterContext.Provider>;
};
